/**
 * Creator labels — admin-set flags on the `creator` row (migration 0045) that carry a
 * rule for every account of that creator, set at onboarding or later in Accounts → Edit.
 *
 * Today's only label is `trans`: the Post Bot puts Maloum's "trans" category on every post
 * it schedules for the creator. The LLM categorizer is told never to infer gender identity
 * (deploy/translator/src/captions.js), so without the label the category is never chosen.
 *
 * Adding a label means: extend CREATOR_LABELS + CREATOR_LABEL_META here, widen the DB CHECK
 * (chk_creator_labels) in a new migration, and give it a rule in
 * apps/api/src/modules/post-bot/creator-labels.ts.
 */
export const CREATOR_LABELS = ['trans'] as const;
export type CreatorLabel = (typeof CREATOR_LABELS)[number];

export const CREATOR_LABEL_META: Record<CreatorLabel, { title: string; description: string }> = {
  trans: {
    title: 'Trans',
    description: 'Every post the Post Bot schedules for this creator carries Maloum’s “trans” category.',
  },
};

export function isCreatorLabel(value: unknown): value is CreatorLabel {
  return typeof value === 'string' && (CREATOR_LABELS as readonly string[]).includes(value);
}

/**
 * Validate + dedupe a labels input (API body, CSV cell). Returns null when the value is not an
 * array of known labels, so callers can answer 400 with the allowed list.
 */
export function normalizeCreatorLabels(value: unknown): CreatorLabel[] | null {
  if (!Array.isArray(value) || value.length > CREATOR_LABELS.length * 4) return null;
  const labels: CreatorLabel[] = [];
  for (const item of value) {
    const label = typeof item === 'string' ? item.trim().toLowerCase() : item;
    if (!isCreatorLabel(label)) return null;
    if (!labels.includes(label)) labels.push(label);
  }
  return labels;
}
